/**
 * Retorna o dia da semana e minutos desde meia-noite no timezone especificado.
 * Usa a Intl API nativa — sem dependências externas.
 */
export function getLocalTimeParts(timezone: string): {
  weekday: number // 0 = Domingo, 6 = Sábado
  minutesFromMidnight: number
} {
  const now = new Date()
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
    hour12: false
  })

  const parts = formatter.formatToParts(now)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''

  const weekdayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6
  }

  const weekday = weekdayMap[get('weekday')] ?? 0
  let hour = parseInt(get('hour'), 10)
  // Intl pode retornar 24 ao invés de 0 para meia-noite em alguns locales
  if (hour === 24) hour = 0
  const minute = parseInt(get('minute'), 10)

  return { weekday, minutesFromMidnight: hour * 60 + minute }
}

/**
 * Converte "HH:MM" em minutos desde meia-noite.
 */
export function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + (m ?? 0)
}

/**
 * Janela de horário local, espelho de isWithinLocalTimeWindow da API (F-24): start > end cruza a
 * meia-noite (22:00–02:00) e a parte depois da meia-noite pertence ao dia em que a janela começou.
 */
export function isWithinLocalWindow(
  window: { startTime?: string; endTime?: string; days?: number[] },
  parts: { weekday: number; minutesFromMidnight: number }
): boolean {
  const { weekday, minutesFromMidnight } = parts
  const start = window.startTime ? timeToMinutes(window.startTime) : undefined
  const end = window.endTime ? timeToMinutes(window.endTime) : undefined

  let windowDay = weekday
  if (start !== undefined && end !== undefined && start > end) {
    if (minutesFromMidnight < start && minutesFromMidnight > end) return false
    if (minutesFromMidnight <= end) windowDay = (weekday + 6) % 7
  } else {
    if (start !== undefined && minutesFromMidnight < start) return false
    if (end !== undefined && minutesFromMidnight > end) return false
  }

  return !window.days || window.days.length === 0 || window.days.includes(windowDay)
}

/**
 * Verifica se um slot compilado está ativo agora com base em dayparting.
 */
export function isSlotActiveNow(
  slot: { startsAtTime?: string; endsAtTime?: string; daysOfWeek?: number[] },
  timezone: string
): boolean {
  return isWithinLocalWindow(
    { startTime: slot.startsAtTime, endTime: slot.endsAtTime, days: slot.daysOfWeek },
    getLocalTimeParts(timezone)
  )
}

export function isPlaylistItemActiveNow(
  item: {
    isEnabled?: boolean
    activeDays?: number[]
    activeTimeStart?: string
    activeTimeEnd?: string
    activeFrom?: string
    activeUntil?: string
  },
  timezone: string
): boolean {
  if (item.isEnabled === false) {
    return false
  }

  const now = new Date()

  if (item.activeFrom && now < new Date(item.activeFrom)) {
    return false
  }

  if (item.activeUntil && now >= new Date(item.activeUntil)) {
    return false
  }

  return isWithinLocalWindow(
    { startTime: item.activeTimeStart, endTime: item.activeTimeEnd, days: item.activeDays },
    getLocalTimeParts(timezone)
  )
}
